import React, { useMemo } from 'react';
import axios, { AxiosResponse } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Configuration,
  QuotesApi,
  QuotesV2Api,
  ListOldQuotesResult as ListQuotesResult,
  QuoteUpdate,
  QuoteGet,
  QuoteCreate,
  GetQuoteFilter,
  OldQuoteGet,
  ListOldQuotesResult,
  ListQuotesV2Result,
  QuoteV2Get,
} from '../pricing-tool-api';
import ProvidedServices from './ProvidedServices';
import Contextualizer from './Contextualizer';

const DEFAULT_SIZE = 20;
const DEFAULT_OFFSET = 0;

export interface IQuoteService {
  createQuote(quoteCreate: QuoteCreate): Promise<AxiosResponse<QuoteGet>>;

  listQuotes(
    pageSize?: number,
    offset?: number,
    filter?: GetQuoteFilter,
  ): Promise<AxiosResponse<ListOldQuotesResult>>;

  getQuote(
    quoteId: string,
    includeDemoData: boolean,
  ): Promise<AxiosResponse<OldQuoteGet>>;

  searchDealName(
    dealNameQuery: string,
  ): Promise<AxiosResponse<Array<string | null>>>;

  searchClientName(
    clientNameQuery: string,
  ): Promise<AxiosResponse<Array<string | null>>>;

  createQuoteV2(quoteCreate: QuoteCreate): Promise<AxiosResponse<QuoteV2Get>>;

  listQuotesV2(
    pageSize?: number,
    offset?: number,
    filter?: GetQuoteFilter,
  ): Promise<AxiosResponse<ListQuotesV2Result>>;

  updateQuoteV2(
    quoteId: string,
    quoteUpdate: QuoteUpdate,
  ): Promise<AxiosResponse<QuoteV2Get>>;

  getQuoteV2(
    quoteId: string,
    includeDemoData: boolean,
  ): Promise<AxiosResponse<QuoteV2Get>>;

  searchDealNameV2(
    dealNameQuery: string,
  ): Promise<AxiosResponse<Array<string | null>>>;

  searchClientNameV2(
    clientNameQuery: string,
  ): Promise<AxiosResponse<Array<string | null>>>;
}

const QuoteServiceContext = Contextualizer.createContext(
  ProvidedServices.QuoteService,
);
export const useQuoteService = () =>
  Contextualizer.use<IQuoteService>(ProvidedServices.QuoteService);

type QuoteServiceProps = { children: React.ReactNode };

function QuoteService({ children }: QuoteServiceProps) {
  const { getAccessTokenSilently } = useAuth0();
  const basePath = `${window.location.origin}/api`;

  const quoteService = {
    async createQuote(
      quoteCreate: QuoteCreate,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<QuoteGet>> {
      const tokenValue = await token;
      return new QuotesApi(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).createQuoteQuotesPost(quoteCreate);
    },

    async listQuotes(
      pageSize = DEFAULT_SIZE,
      offset = DEFAULT_OFFSET,
      filter: GetQuoteFilter = {},
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<ListQuotesResult>> {
      const tokenValue = await token;
      return new QuotesApi(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).listQuotesQuotesGet(pageSize, offset, JSON.stringify(filter));
    },

    async getQuote(
      quoteId: string,
      includeDemoData = false,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<OldQuoteGet>> {
      const tokenValue = await token;
      return new QuotesApi(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).getQuoteQuotesQuoteIdGet(quoteId, includeDemoData);
    },

    async searchDealName(
      dealNameQuery: string,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<Array<string | null>>> {
      const tokenValue = await token;
      return new QuotesApi(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).searchDealNameQuotesSearchDealGet(dealNameQuery);
    },

    async searchClientName(
      clientNameQuery: string,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<Array<string | null>>> {
      const tokenValue = await token;
      return new QuotesApi(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).searchClientNameQuotesSearchClientGet(clientNameQuery);
    },

    // V2 Quote methods
    async createQuoteV2(
      quoteCreate: QuoteCreate,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<QuoteV2Get>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).createQuoteV2QuotesV2Post(quoteCreate);
    },

    async listQuotesV2(
      pageSize = DEFAULT_SIZE,
      offset = DEFAULT_OFFSET,
      filter: GetQuoteFilter = {},
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<ListQuotesV2Result>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).listQuotesV2QuotesV2Get(pageSize, offset, JSON.stringify(filter));
    },

    async updateQuoteV2(
      quoteId: string,
      quoteUpdate: QuoteUpdate,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<QuoteV2Get>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).updateQuoteV2QuotesV2QuoteIdPut(quoteId, quoteUpdate);
    },

    async getQuoteV2(
      quoteId: string,
      includeDemoData = false,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<QuoteV2Get>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).getQuoteV2QuotesV2QuoteIdGet(quoteId, includeDemoData);
    },

    async searchDealNameV2(
      dealNameQuery: string,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<Array<string | null>>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).searchDealNameV2QuotesV2SearchDealGet(dealNameQuery);
    },

    async searchClientNameV2(
      clientNameQuery: string,
      token = getAccessTokenSilently(),
    ): Promise<AxiosResponse<Array<string | null>>> {
      const tokenValue = await token;
      return new QuotesV2Api(
        new Configuration({ basePath, accessToken: tokenValue }),
      ).searchClientNameV2QuotesV2SearchClientGet(clientNameQuery);
    },
  };

  return (
    <QuoteServiceContext.Provider
      value={useMemo(() => quoteService, [getAccessTokenSilently, basePath])}
    >
      {children}
    </QuoteServiceContext.Provider>
  );
}

export default QuoteService;
